import "../style.scss";

import { getDatePickerEndAdornmentSx, StyledDatePicker } from "components/shared/StyledDatePicker";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { FocusEventHandler } from "react";
import { forwardRef } from "react";
import type { Validate } from "react-hook-form";
import { Controller, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface DateFormComponentProps {
    label: string;
    control: any;
    controlName: string;
    error?: any;
    required?: boolean;
    ref?: any;
    size?: "small" | "medium";
    sx?: any;
    onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    disabled?: boolean;
    /** Shown on the lock icon tooltip when the field is disabled. */
    disabledText?: string;
    /**
     * Maximum number of years in future that are displayed. By default, only on year.
     */
    maxYearDistance?: number;
    /**
     * Helps set endDate of current period to know from where to determine maxYearDistance. By default, it is current day.
     */
    maxDate?: Date;
    validate?: Validate<any, any>;
}

const DateFormComponent = forwardRef<any, DateFormComponentProps>(function DateFormComponent(
    props: DateFormComponentProps,
    ref,
) {
    const {
        label,
        control,
        controlName,
        error,
        required,
        size,
        sx,
        onFocus,
        disabled,
        disabledText,
        maxYearDistance = 1,
        maxDate,
        validate,
    } = props;
    const [t] = useTranslation();

    return (
        <div className="form-component">
            <Controller
                name={controlName}
                control={control}
                rules={{
                    required: required ? t("NewElement.Required") : undefined,
                    validate: (date, formValues) => {
                        if (date?.toString() === "Invalid Date") return t("NewElement.IncorrectDate");
                        // else if (date > new Date()) return t("NewElement.Future")
                        if (validate) return validate(date, formValues);
                        return true;
                    },
                }}
                render={({ field: { ref: fieldRef, ...rest } }) => {
                    return (
                        <>
                            <label className="form-component-label" htmlFor={controlName}>
                                {label}
                                {required && label && "*"}
                            </label>
                            <StyledDatePicker
                                {...rest}
                                inputRef={fieldRef}
                                ref={ref}
                                disabled={disabled}
                                inputSize={size}
                                maxYearDistance={maxYearDistance}
                                maxDate={maxDate}
                                disabledText={disabledText}
                                renderInput={(params) => (
                                    <StyledTextField
                                        {...params}
                                        name={controlName}
                                        id={controlName}
                                        error={!!error}
                                        fullWidth
                                        size={size}
                                        disabled={disabled}
                                        disabledText={disabledText}
                                        helperText={error?.message ?? ""}
                                        sx={{ ...getDatePickerEndAdornmentSx(size), ...sx }}
                                        onFocus={onFocus}
                                    />
                                )}
                            />
                        </>
                    );
                }}
            />
        </div>
    );
});

export default DateFormComponent;
